import { Box, Flex, Divider, Text, Tooltip, Center } from "@chakra-ui/react";
import { AppContext } from "AppProvider/AppContext";
import React, { useContext, useEffect, useState } from "react";
import { FaFile, FaFileImage } from "react-icons/fa";
import { formatDateTime, DDMMYYYY_FORMAT } from "utils/dateUtils/dateUtils";
import UploadDocumentModal from "../UploadDocumentModal/UploadDocumentModal";
import { PlusSquareIcon } from "@chakra-ui/icons";
import { keyframes } from "@emotion/react";
import { appRequest } from "routes";
import { getForeignLabel } from "../CommentDrawer/function";
import FileViewerModal from "../ViewerModal/FileViewerModel";

type ApprovavalProgessProps = {
  selectedFile: any;
  setSelectedFile: React.Dispatch<React.SetStateAction<any>>;
  approvalHistory: any;
  name: any;
  id: any;
  handleGet: () => void;
  onClose: () => void;
  handleOpenViewer?: () => void;
  initiatedBy?: any;
  isDuplicate?: any
};

function ApprovalHistoryProgress({
  selectedFile,
  setSelectedFile,
  approvalHistory,
  name,
  handleGet,
  id,
  handleOpenViewer,
  onClose,
  initiatedBy,
  isDuplicate
}: ApprovavalProgessProps) {
  const { workflowProcess } = useContext(AppContext)
  const [isUploadOpen, setIsUploadOpen] = useState<boolean>(false);
  const [selectedIndex, setSelectedIndex] = useState<number>();
  const { accountDetails } = useContext(AppContext) || {};
  const [isHovered, setIsHovered] = useState<any>(null);
  const [approvalHistorys, setApprovalHistory] = useState<any[]>([]);
  const [initiatorUser, setInitiatorUser] = useState<any>({});
  const [workFlowData, setWorkFlowData] = useState<any>({});
  const [paymentUtrDetails, setPaymentUtrDetails] = useState<any>({});
  const [isLocalViewerOpen, setIsLocalViewerOpen] = useState<boolean>(false);
  const [localViewerFile, setLocalViewerFile] = useState<any>(null);
  const getDocumentApproval = async () => {
    try {
      const response: any = await appRequest(
        "document",
        "getDocumentApproval",
        { _id: id }
      );
      if (response?.status === "success") {
        setWorkFlowData(response?.data?.values || {});
        setInitiatorUser(response?.data?.initiatedBy || {});
        setApprovalHistory(response?.data?.approvals || []);
        setPaymentUtrDetails({
          paymentInitiatedDate: response?.data?.paymentInitiatedDate,
          paymentUtrNumber: response?.data?.paymentUtrNumber,
          paymentStatus: response?.data?.paymentStatus,
          paymentFile: response?.data?.paymentFile
        })
      }
    } catch (err: any) {
      console.error("Failed to load approval history");
      setWorkFlowData({});
      setApprovalHistory([]);
    }
  };

  useEffect(() => {
    if (id) getDocumentApproval();
  }, [id]);

  const handleMouseEnter = (index: number) => setIsHovered(index);
  const handleMouseLeave = () => setIsHovered(null);

  const handleOpenUploadModal = () => setIsUploadOpen(true);
  const handleCloseUploadModal = () => {
    setIsUploadOpen(false);
    onClose?.();
  };

  // Safe username extractor — prevents .split() crash
  const safeUsername = (user: any) => {
    if (!user?.username) return "Unknown";
    const parts = String(user.username).split("@");
    return parts[0] || "Unknown";
  };

  function getEnhancedApprovalHistory(history: any[] = []) {
    if (!Array.isArray(history)) return [];

    const enhanced = history.map((entry: any, index: number, arr: any[]) => {
      let newStatus = entry.status || "pending";
      let date = entry.createdAt || entry.updatedAt;

      if (index === 0) {
        newStatus = "Initiator";
      } else if (
        entry.fileId &&
        arr[index - 1]?.fileId &&
        entry.fileId !== arr[index - 1].fileId
      ) {
        newStatus = "reuploaded";
        date = entry.updatedAt || date;
      }

      return {
        ...entry,
        enhancedStatus: newStatus,
        date: date || new Date().toISOString(),
      };
    });

    const lastEntry = enhanced[enhanced.length - 1];
    const maxLevel = workFlowData?.levels
      ? `level-${workFlowData.levels}`
      : null;

    // if (
    //   enhanced.length > 0 &&
    //   workFlowData?.fileViewer?.showApprHistory &&
    //   !(lastEntry?.status === "approved" && lastEntry?.level === maxLevel)
    // ) {
    //   let nextLevel: string | null = null;
    //   if (lastEntry?.status === "approved") {
    //     const curr = parseInt((lastEntry.level || "").split("-")[1] || "0");
    //     nextLevel = `level-${curr + 1}`;
    //   } else if (lastEntry?.status === "rejected") {
    //     const curr = parseInt((lastEntry.level || "").split("-")[1] || "1");
    //     nextLevel = `level-${Math.max(1, curr - 1)}`;
    //   }

    //   let nextUser = null;
    //   if (nextLevel === "level-1" && history[0]?.user) {
    //     nextUser = history[0].user;
    //   } else if (nextLevel && Array.isArray(workFlowData?.approvals)) {
    //     nextUser =
    //       workFlowData.approvals.find((a: any) => a.level === nextLevel) ||
    //       null;
    //   }

    //   enhanced.push({
    //     ...lastEntry,
    //     status: "pending",
    //     enhancedStatus: "pending",
    //     level: nextLevel,
    //     user: nextUser,
    //     date: new Date().toISOString(),
    //   });
    // }

    if (
      enhanced.length > 0 &&
      workFlowData?.fileViewer?.showApprHistory &&
      !(lastEntry?.status === "approved" && lastEntry?.level === maxLevel)
    ) {
      let nextLevel: string | null = null;

      if (lastEntry?.status === "approved") {
        const curr = parseInt((lastEntry.level || "").split("-")[1] || "0");
        nextLevel = `level-${curr + 1}`;
      } else if (lastEntry?.status === "rejected") {
        const curr = parseInt((lastEntry.level || "").split("-")[1] || "1");
        nextLevel = `level-${Math.max(1, curr - 1)}`;
      }

      let nextUser = null;

      if (nextLevel === "level-1" && history[0]?.user) {
        nextUser = history[0].user;
      } else if (nextLevel && Array.isArray(workFlowData?.approvals)) {
        nextUser =
          workFlowData.approvals.find((a: any) => a.level === nextLevel) || null;
      }

      const firstUserId = history[0]?.user?._id || history[0]?.user?.id;
      const lastUserId = lastEntry?.user?._id || lastEntry?.user?.id;

      const isSameUser = firstUserId && lastUserId && firstUserId === lastUserId;
      const isRejected = lastEntry?.status === "rejected";

      // 🚀 Your Custom Condition
      if (!(isSameUser && isRejected)) {
        enhanced.push({
          ...lastEntry,
          status: "pending",
          enhancedStatus: "pending",
          level: nextLevel,
          user: nextUser,
          date: new Date().toISOString()
        });
      }
    }

    const isFullyApproved = lastEntry?.status === "approved" && lastEntry?.level === maxLevel;
    const isForeign = workflowProcess?.processName === "foreign";

    if (isFullyApproved && isForeign && !isDuplicate) {
      const isDone = paymentUtrDetails?.paymentStatus === "done";
      enhanced.push({
        status: isDone ? "done" : "pending",
        enhancedStatus: isDone ? "payment done" : "payment pending",
        isPayment: true,
        paymentUtrNumber: paymentUtrDetails?.paymentUtrNumber,
        file: paymentUtrDetails?.paymentFile ? { _id: paymentUtrDetails.paymentFile } : null,
        date: paymentUtrDetails?.paymentInitiatedDate || new Date().toISOString(),
      });
    }

    return enhanced;
  }

  const enhancedHistory = getEnhancedApprovalHistory(approvalHistorys);

  useEffect(() => {
    if (enhancedHistory.length > 0) {
      setSelectedIndex(enhancedHistory.length - 1);
    }
  }, [enhancedHistory.length]);

  const rippleAnimation = keyframes`
    0% { transform: scale(1); opacity: 0.8; }
    50% { transform: scale(1.75); opacity: 0.5; }
    100% { transform: scale(2.5); opacity: 0.1; }
  `;

  const getStatus = (message: any, status: any) => {
    try {
      const text = String(message || "").toLowerCase();
      const fallbackStatus = String(status || "").toLowerCase();

      const hasSystem = /(auto|system)/i.test(text);
      const isApproved = /approved/i.test(text);
      const isRejected = /rejected/i.test(text);

      // ✅ System-based decision (highest priority)
      if (hasSystem && isApproved) return "Auto Approved";
      if (hasSystem && isRejected) return "Auto Rejected";

      // ❌ No system keyword → trust second parameter
      if (/rejected/i.test(fallbackStatus)) return "Rejected";
      if (/approved/i.test(fallbackStatus)) return "Approved";

      return "Pending";
    } catch (err) {
      return "Pending";
    }
  };



  return (
    <>
      <Box
        h={{ base: "30vh", md: "40vh", lg: "78vh" }}
        overflow="auto"
        className="customScrollBar"
        paddingTop={5}
      >
        {enhancedHistory.length === 0 ? (
          <Box>
            <Center h="70vH">
              <Text fontWeight="500" color="red" fontSize="lg">
                No approval data found.
              </Text>
            </Center>
          </Box>
        ) : (
          enhancedHistory.map((history: any, index: number) => (
            <Box
              key={index}
              onMouseEnter={() => handleMouseEnter(index)}
              // onMouseLeave={() => handleMouseLeave(index)}
              onClick={() => {
                if (history.status !== "pending" && history.file) {
                  if (history.isPayment) {
                    setLocalViewerFile(history.file);
                    setIsLocalViewerOpen(true);
                  } else {
                    setSelectedFile(history.file);
                    setSelectedIndex(index);
                    handleOpenViewer?.();
                  }
                }
              }}
              cursor={history.status === "pending" ? "default" : "pointer"}
            >
              <Flex
                alignItems="center"
                gap={5}
                bg={
                  selectedIndex === index
                    ? "#fff7d1"
                    : index === isHovered
                      ? "#ffeb99"
                      : "none"
                }
                padding={2}
                rounded={8}
                my={-3.5}
              >
                {history.status === "pending" ? (
                  <Box
                    bg="teal.500"
                    borderRadius="50%"
                    justifyContent="center"
                    alignContent="center"
                    width="0.4cm"
                    height="0.4cm"
                    zIndex="1"
                    ml="0.35rem"
                  >
                    <Box
                      bg="teal.300"
                      cursor="pointer"
                      borderRadius="50%"
                      width="0.4cm"
                      height="0.4cm"
                      animation={`${rippleAnimation} 1s linear infinite`}
                      zIndex="-1"
                      transform="translate(-50%, -50%)"
                    />
                  </Box>
                ) : (
                  <Tooltip
                    label={history.isPayment ? "Payment Details" : "View Document"}
                    hasArrow
                    arrowSize={15}
                    textAlign="center"
                  >
                    <Box color="teal.400">
                      {history?.file?.metadata?.contentType?.includes(
                        "image"
                      ) ? (
                        <FaFileImage fontSize="1.6rem" />
                      ) : (
                        <FaFile fontSize="1.6rem" />
                      )}
                    </Box>
                  </Tooltip>
                )}

                {history.status === "pending" ? (
                  <Box>
                    <Text
                      textTransform="capitalize"
                      fontWeight="500"
                      fontSize="sm"
                      color="black"
                    >
                      {history.isPayment ? (
                        "Payment Pending"
                      ) : workflowProcess?.processName === "foreign"
                        ? `${getForeignLabel(history.level)} (${safeUsername(history.user)}) :- pending`
                        : history.user
                          ? ((accountDetails?.level === "level-1" || accountDetails?.level === "level-2") && name === "Pending Approval" && workflowProcess?.processName === "pr") ? "FI User :- pending" : `${safeUsername(history.user)} :- pending`
                          : `${history.level || "Next"} :- pending`}
                    </Text>
                  </Box>
                ) : (
                  <Tooltip
                    label={
                      history.isPayment
                        ? history.enhancedStatus === "payment pending"
                          ? "Payment Pending"
                          : `Payment Done ${history.paymentUtrNumber ? `- UTR: ${history.paymentUtrNumber}` : ""}`
                        : `${safeUsername(history.user)} :- ${(history?.comment || history.enhancedStatus)
                          ?.replace(/auto[-\s]?approve(d)?/gi, "auto skipped")
                          ?.replace(/auto[-\s]?reject(ed)?/gi, "auto skipped")}`
                    }
                    hasArrow
                    arrowSize={15}
                    textAlign="center"
                    textTransform="capitalize"
                  >
                    <Box>
                      <Text
                        textTransform="capitalize"
                        fontWeight="500"
                        fontSize="sm"
                        color={
                          history.enhancedStatus === "approved" || history.enhancedStatus === "payment done"
                            ? "green.500"
                            : history.enhancedStatus === "rejected"
                              ? "red.500"
                              : "black"
                        }
                      >
                        {history.isPayment ? (
                          history.enhancedStatus === "payment pending"
                            ? "Payment Pending"
                            : `Payment Done ${history.paymentUtrNumber ? `- UTR: ${history.paymentUtrNumber}` : ""}`
                        ) : index === 0 && initiatorUser
                          ? safeUsername(initiatorUser)
                          : workflowProcess?.processName === "foreign"
                            ? `${getForeignLabel(history.level)} (${safeUsername(history.user)}) :-  ${((s) => ["auto approved", "Auto Approved", "Auto Rejected", "auto rejected"].includes(s) ? `auto skipped (${s.toLowerCase().includes("approved") ? "approved" : "rejected"})` : s)(getStatus(history.comment, history.enhancedStatus))}`
                            : `${safeUsername(history.user) ||
                            history.level ||
                            "Unknown"
                            } :-  ${((s) => ["auto approved", "Auto Approved", "Auto Rejected", "auto rejected"].includes(s) ? `auto skipped (${s.toLowerCase().includes("approved") ? "approved" : "rejected"})` : s)(getStatus(history.comment, history.enhancedStatus))}`}
                      </Text>

                      <Text fontWeight="500" color="gray.500" fontSize="xs">
                        {history.date
                          ? formatDateTime(history.date, DDMMYYYY_FORMAT, true)
                          : "-"}
                      </Text>
                    </Box>
                  </Tooltip>
                )}
              </Flex>

              {index < enhancedHistory.length - 1 && (
                <Divider
                  orientation="vertical"
                  h="35px"
                  ml="1.2rem"
                  bgColor="gray"
                  w="1.5px"
                />
              )}

              {index === enhancedHistory.length - 1 &&
                accountDetails?.role === "uploader" &&
                name === "Pending Approval" && (
                  <>
                    <Divider
                      orientation="vertical"
                      h="35px"
                      ml="1.2rem"
                      bgColor="gray"
                      w="1.5px"
                    />
                    <Tooltip
                      label="Re-Upload Document"
                      hasArrow
                      arrowSize={15}
                      textAlign="center"
                    >
                      <Box color="teal.400" ml="0.6rem" w="1.5rem">
                        <PlusSquareIcon
                          fontSize="1.5rem"
                          cursor="pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenUploadModal();
                          }}
                        />
                      </Box>
                    </Tooltip>
                  </>
                )}
            </Box>
          ))
        )}
      </Box>

      <FileViewerModal
        isOpen={isLocalViewerOpen}
        onClose={() => setIsLocalViewerOpen(false)}
        file={localViewerFile}
      />

      <UploadDocumentModal
        isOpen={isUploadOpen}
        onClose={handleCloseUploadModal}
        type="reupload"
        id={id}
        handleGet={handleGet}
      />
    </>
  );
}

export default ApprovalHistoryProgress;